import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  static #PRODUCT_SECTION_ID = 'main';
  /** @type {Map<string, Element>} */
  static #sharedCachedContent = new Map();
  /** @type {Map<string, Promise<Document | null>>} */
  static #sharedInflightFetches = new Map();
  /** @type {IntersectionObserver | null} */
  static #prefetchObserver = null;
  /** @type {boolean} */
  #isHandlingClick = false;
  /** @type {boolean} */
  #userSelectedVariantInModal = false;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {AbortController} */
  #touchAbortController = new AbortController();
  /** @type {{ x: number; y: number; button: HTMLButtonElement } | null} */
  #touchStart = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  /**
   * Builds a Section Rendering API URL for the product information section.
   * @param {string} productPageUrl
   * @returns {string}
   */
  #getProductSectionFetchUrl(productPageUrl) {
    const url = new URL(productPageUrl, window.location.origin);
    url.searchParams.set('section_id', QuickAddComponent.#PRODUCT_SECTION_ID);

    return url.toString();
  }

  /**
   * Registers viewport-based prefetching for choose-option buttons.
   */
  static #ensurePrefetchObserver() {
    if (QuickAddComponent.#prefetchObserver) return;

    QuickAddComponent.#prefetchObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const component = entry.target;
          if (!(component instanceof QuickAddComponent)) continue;

          component.#prefetchProductPage();
          QuickAddComponent.#prefetchObserver?.unobserve(component);
        }
      },
      { rootMargin: '300px 0px' }
    );
  }

  connectedCallback() {
    super.connectedCallback();

    this.#touchAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });

    if (this.dataset.quickAddButton === 'choose') {
      this.addEventListener('pointerenter', this.#prefetchProductPage);
      this.addEventListener('focusin', this.#prefetchProductPage);
      this.addEventListener('pointerdown', this.#prefetchProductPage);

      QuickAddComponent.#ensurePrefetchObserver();
      QuickAddComponent.#prefetchObserver?.observe(this);
    }

    this.addEventListener('touchstart', this.#handleTouchStart, {
      signal: this.#touchAbortController.signal,
      passive: true,
      capture: true,
    });
    this.addEventListener('touchend', this.#handleTouchEnd, {
      signal: this.#touchAbortController.signal,
      passive: false,
      capture: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerenter', this.#prefetchProductPage);
    this.removeEventListener('focusin', this.#prefetchProductPage);
    this.removeEventListener('pointerdown', this.#prefetchProductPage);
    QuickAddComponent.#prefetchObserver?.unobserve(this);
    this.#cartUpdateAbortController.abort();
    this.#touchAbortController.abort();
  }

  /**
   * Records touch start for iOS tap detection on quick-add buttons.
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.quick-add__button');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const touch = event.changedTouches[0] ?? event.touches[0];
    if (!touch) return;

    this.#touchStart = {
      x: touch.clientX,
      y: touch.clientY,
      button,
    };
  };

  /**
   * iOS Safari often fails to synthesize click events on nested quick-add buttons.
   * Handle the tap directly and suppress the delayed ghost click.
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (!this.#touchStart) return;

    const touch = event.changedTouches[0];
    if (!touch) {
      this.#touchStart = null;
      return;
    }

    const { x, y, button } = this.#touchStart;
    this.#touchStart = null;

    const moved = Math.abs(touch.clientX - x) > 10 || Math.abs(touch.clientY - y) > 10;
    if (moved) return;

    if (!(event.target instanceof Element) || !event.target.closest('.quick-add__button')) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.classList.contains('quick-add__button--choose')) {
      this.handleClick(event);
      return;
    }

    if (button.classList.contains('quick-add__button--add')) {
      button.click();
    }
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    QuickAddComponent.#sharedCachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;

    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Prefetches product page content for the choose-options flow.
   */
  #prefetchProductPage = () => {
    const cacheKey = this.#getCacheKey(this.productPageUrl);

    if (!cacheKey || QuickAddComponent.#sharedCachedContent.has(cacheKey)) return;

    this.fetchProductPage(this.productPageUrl);
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isHandlingClick) return;
    this.#isHandlingClick = true;

    const currentUrl = this.productPageUrl;
    const cacheKey = this.#getCacheKey(currentUrl);

    this.#updateViewProductDetailsLink();
    this.#openQuickAddModal();

    try {
      let productGrid = QuickAddComponent.#sharedCachedContent.get(cacheKey);

      if (!productGrid) {
        this.#setModalLoading(true);

        try {
          await this.fetchProductPage(currentUrl);
          productGrid = QuickAddComponent.#sharedCachedContent.get(cacheKey);
        } finally {
          this.#setModalLoading(false);
        }
      }

      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      }
    } finally {
      this.#isHandlingClick = false;
    }
  };

  /**
   * @param {boolean} isLoading
   */
  #setModalLoading(isLoading) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.toggleAttribute('data-loading', isLoading);
    modalContent.setAttribute('aria-busy', String(isLoading));
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#resetModalContentScroll();

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #resetModalContentScroll() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    modalContent.scrollTop = 0;
    this.#userSelectedVariantInModal = false;
  }

  /**
   * Scrolls the mobile quick-add modal to reveal the product image after variant selection.
   * @param {Element} modalContent
   */
  #scrollModalToMedia(modalContent) {
    if (!isMobileBreakpoint()) return;

    const media = modalContent.querySelector('.product-information__media');
    if (media instanceof HTMLElement) {
      requestAnimationFrame(() => {
        media.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  #updateViewProductDetailsLink() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.updateViewProductDetailsLink(this.productPageUrl);
  }

  /**
   * Normalizes a product URL for cache lookups.
   * @param {string} productPageUrl
   * @returns {string}
   */
  #getCacheKey(productPageUrl) {
    if (!productPageUrl) return '';

    const url = new URL(productPageUrl, window.location.origin);
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');

    return url.toString();
  }

  /**
   * Finds the quick-add product grid in fetched HTML.
   * @param {string} responseText
   * @returns {Element | null}
   */
  #findProductGridElement(responseText) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    let gridElement = html.querySelector('[data-product-grid-content]');

    if (gridElement) return gridElement;

    const wrappedHtml = new DOMParser().parseFromString(`<body>${responseText}</body>`, 'text/html');

    return wrappedHtml.querySelector('[data-product-grid-content]');
  }

  /**
   * Parses and caches product grid content when available.
   * @param {string} responseText
   * @param {string} cacheKey
   * @returns {boolean}
   */
  #tryCacheProductGrid(responseText, cacheKey) {
    const gridElement = this.#findProductGridElement(responseText);
    if (!gridElement) return false;

    QuickAddComponent.#sharedCachedContent.set(
      cacheKey,
      /** @type {Element} */ (gridElement.cloneNode(true))
    );

    return true;
  }

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  async #fetchResponseText(url) {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    return response.text();
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const cacheKey = this.#getCacheKey(productPageUrl);
    const inflightFetch = QuickAddComponent.#sharedInflightFetches.get(cacheKey);

    if (inflightFetch) return inflightFetch;

    const fetchPromise = (async () => {
      try {
        if (QuickAddComponent.#sharedCachedContent.has(cacheKey)) {
          return null;
        }

        const sectionFetchUrl = this.#getProductSectionFetchUrl(productPageUrl);
        const fullPageUrl = cacheKey;

        try {
          const sectionResponseText = await this.#fetchResponseText(sectionFetchUrl);

          if (this.#tryCacheProductGrid(sectionResponseText, cacheKey)) {
            return new DOMParser().parseFromString(sectionResponseText, 'text/html');
          }
        } catch (error) {
          console.warn('[quick-add] Section prefetch failed, falling back to full product page.', error);
        }

        const fullPageResponseText = await this.#fetchResponseText(fullPageUrl);
        this.#tryCacheProductGrid(fullPageResponseText, cacheKey);

        return new DOMParser().parseFromString(fullPageResponseText, 'text/html');
      } finally {
        QuickAddComponent.#sharedInflightFetches.delete(cacheKey);
      }
    })();

    QuickAddComponent.#sharedInflightFetches.set(cacheKey, fetchPromise);

    return fetchPromise;
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productMedia = productGrid.querySelector('.product-information__media');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (productMedia) {
        productGrid.appendChild(productMedia);
      }

      productDetails?.remove();
    }

    this.#userSelectedVariantInModal = false;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

    queueMicrotask(() => {
      requestAnimationFrame(() => {
        this.#configureModalGallery(modalContent, { scrollThumbnailIntoView: false });
        this.#resetModalContentScroll();
      });
    });
  }

  /**
   * Keeps quick-add modal gallery to one visible slide on mobile/tablet.
   * @param {Element} modalContent - The modal content element
   * @param {{ scrollThumbnailIntoView?: boolean }} [options]
   */
  #configureModalGallery(modalContent, options = {}) {
    const { scrollThumbnailIntoView = true } = options;

    if (window.matchMedia('(max-width: 989px)').matches === false) return;

    if (modalContent.dataset.quickAddGalleryVariantListener !== 'true') {
      modalContent.dataset.quickAddGalleryVariantListener = 'true';

      modalContent.addEventListener(
        'change',
        (event) => {
          if (!event.isTrusted) return;
          if (!(event.target instanceof Element)) return;
          if (!event.target.closest('variant-picker')) return;

          this.#userSelectedVariantInModal = true;
        },
        { capture: true }
      );

      modalContent.addEventListener(
        'click',
        (event) => {
          if (!event.isTrusted) return;
          if (!(event.target instanceof Element)) return;
          if (
            !event.target.closest(
              'variant-picker input, variant-picker select, variant-picker label, variant-picker .variant-option__button-label'
            )
          ) {
            return;
          }

          this.#userSelectedVariantInModal = true;
        },
        { capture: true }
      );

      modalContent.addEventListener(ThemeEvents.variantUpdate, () => {
        queueMicrotask(() => {
          requestAnimationFrame(() => {
            this.#configureModalGallery(modalContent, { scrollThumbnailIntoView: false });

            if (this.#userSelectedVariantInModal) {
              this.#userSelectedVariantInModal = false;
              this.#scrollModalToMedia(modalContent);
            }
          });
        });
      });
    }

    modalContent.querySelectorAll('.product-information__media slideshow-component').forEach((slideshowEl) => {
      if (!(slideshowEl instanceof HTMLElement)) return;

      slideshowEl.setAttribute('data-quick-add-gallery', '');

      /** @param {number} activeIndex */
      const showSlide = (activeIndex) => {
        const currentSlides = Array.from(slideshowEl.querySelectorAll('slideshow-slide'));
        const currentThumbnails = Array.from(
          slideshowEl.querySelectorAll('.media-gallery__mobile-controls .slideshow-control')
        );

        if (!currentSlides.length) return;

        const index = Math.min(Math.max(activeIndex, 0), currentSlides.length - 1);

        currentSlides.forEach((slide, slideIndex) => {
          slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
        });

        currentThumbnails.forEach((thumbnail, thumbIndex) => {
          thumbnail.setAttribute('aria-selected', thumbIndex === index ? 'true' : 'false');
        });

        const scroller = slideshowEl.querySelector('slideshow-slides');
        if (scroller instanceof HTMLElement) {
          scroller.scrollLeft = 0;
          scroller.scrollTop = 0;
        }

        if (!scrollThumbnailIntoView) return;

        const thumbnailsContainer = slideshowEl.querySelector(
          '.media-gallery__mobile-controls .slideshow-controls__thumbnails-container'
        );
        const selectedThumbnail = currentThumbnails[index];

        if (thumbnailsContainer instanceof HTMLElement && selectedThumbnail instanceof HTMLElement) {
          selectedThumbnail.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      };

      if (slideshowEl.dataset.quickAddGalleryConfigured !== 'true') {
        slideshowEl.dataset.quickAddGalleryConfigured = 'true';

        slideshowEl.addEventListener(
          'click',
          (event) => {
            if (!(event.target instanceof Element)) return;

            const thumbnail = event.target.closest('.media-gallery__mobile-controls .slideshow-control');
            if (!thumbnail || !slideshowEl.contains(thumbnail)) return;

            event.preventDefault();
            event.stopPropagation();

            const currentThumbnails = Array.from(
              slideshowEl.querySelectorAll('.media-gallery__mobile-controls .slideshow-control')
            );
            const index = currentThumbnails.indexOf(thumbnail);
            if (index >= 0) showSlide(index);
          },
          { capture: true }
        );

        slideshowEl.addEventListener('slideshow:select', (event) => {
          const index = /** @type {CustomEvent} */ (event).detail?.index;
          if (typeof index !== 'number') return;

          requestAnimationFrame(() => {
            showSlide(index);
          });
        });
      }

      const currentThumbnails = Array.from(
        slideshowEl.querySelectorAll('.media-gallery__mobile-controls .slideshow-control')
      );
      const currentSlides = Array.from(slideshowEl.querySelectorAll('slideshow-slide'));
      const selectedThumbnailIndex = currentThumbnails.findIndex(
        (thumbnail) => thumbnail.getAttribute('aria-selected') === 'true'
      );
      const activeSlideIndex = currentSlides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');

      showSlide(selectedThumbnailIndex >= 0 ? selectedThumbnailIndex : Math.max(activeSlideIndex, 0));
    });
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal: this.#abortController.signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    this.updateViewProductDetailsLink(anchorElement.href);
  };

  /**
   * Updates the footer link to the product page.
   * @param {string} url
   */
  updateViewProductDetailsLink(url) {
    const link = this.querySelector('#quick-add-view-product-details');

    if (link instanceof HTMLAnchorElement && url) {
      link.href = url;
    }
  }

  #handleDialogOpen = () => {
    const { closeButton } = this.refs;
    const modalContent = this.querySelector('#quick-add-modal-content');

    requestAnimationFrame(() => {
      closeButton?.blur();

      if (modalContent instanceof HTMLElement) {
        modalContent.focus({ preventScroll: true });
      }
    });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
